import React from 'react';

import { useIntl } from 'react-intl';

import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useUpdateRole } from 'src/features/roles/api/useUpdateRole';
import { rolesMessages } from 'src/features/roles/utils/messages';
import RoleFormModal from 'src/features/roles/components/forms/RoleFormModal';
import { LIST_ROLES_RESOURCE } from 'src/features/roles/api/useListRoles';
import { Role } from 'src/features/roles/utils/types';
import { useParking } from 'src/providers/ParkingProvider';
import { generalErrorMessages } from 'src/utils/messages';

interface RoleFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedRole: Role;
    children?: React.ReactNode;
}

function UpdateRoleFormModal<TData>({ className, semantics, children, selectedRole }: RoleFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { mutate, isPending } = useUpdateRole({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_ROLES_RESOURCE] });
            toast({
                title: formatMessage(rolesMessages.updateRoleSuccess),
                description: formatMessage(rolesMessages.updateRoleSuccessDescription, {
                    entry: success.name,
                }),
            });
        },
        onError: () => {
            toast({
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description: formatMessage(generalErrorMessages.unknownError),
                variant: 'destructive',
            });
        },
    });

    return (
        <RoleFormModal
            selectedRole={selectedRole}
            className={className}
            mutate={(data, onSuccess, onError) =>
                mutate({ data, roleId: selectedRole.id, parkingId }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(rolesMessages.editRoleSubtitle, {
                entry: selectedRole.name,
            })}
        >
            {children}
        </RoleFormModal>
    );
}

export default UpdateRoleFormModal;
